import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import MiStoreHero from './components/MiStoreHero';
import MiGallerySection from './components/MiGallerySection';
import StoresMap from './components/StoresMap';
import Icon from '../../components/AppIcon';
import SEO from '../../components/SEO';
import themeManager from '../../utils/themeManager';
import { initSmoothScroll } from '../../utils/smoothScroll';

const MiStorePage = () => {
  const { t, i18n } = useTranslation(['store', 'seo']);
  const location = useLocation();

  useEffect(() => {
    initSmoothScroll();
    if (!location.hash) return;

    const id = location.hash.replace('#', '');
    const el = document.getElementById(id);

    if (!el) return;

    setTimeout(() => {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);
  }, [location]);

  const [theme, setTheme] = useState(themeManager.getCurrentTheme());
  const [selectedFilter, setSelectedFilter] = useState('svi');
  const [selectedStore, setSelectedStore] = useState(null);

  useEffect(() => {
    const handleThemeChange = (event) => setTheme(event.detail.theme);
    window.addEventListener('themeChanged', handleThemeChange);
    return () => window.removeEventListener('themeChanged', handleThemeChange);
  }, []);

  // Podaci o prodavnicama preuzeti iz tvog koda
  const belgradStores = useMemo(() => [
    {
      id: 1,
      name: 'Xiaomi Store Ušće',
      address: 'Bulevar Mihajla Pupina 4',
      city: 'Novi Beograd 11070',
      phone: '060/5600-589',
      hours: 'Pon-Ned: 10:00-22:00h',
      coordinates: { lat: 44.8189, lng: 20.4098 },
      region: 'beograd',
      image: '/assets/images/mi-store-usce.webp'
    },
    {
      id: 2,
      name: 'Xiaomi Store Ada Mall',
      address: 'Radnička 9',
      city: 'Čukarica 11030',
      phone: '060/5600-587',
      hours: 'Pon-Ned: 10:00-22:00h',
      coordinates: { lat: 44.7866, lng: 20.4489 },
      region: 'beograd',
      image: '/assets/images/mi-store-ada.webp'
    },
    {
      id: 3,
      name: 'Xiaomi Store BEO',
      address: 'Vojislava Ilića 141i',
      city: 'Beograd 11000',
      phone: '060/5600-597',
      hours: 'Pon-Ned: 10:00-22:00h',
      coordinates: { lat: 44.8420, lng: 20.4016 },
      region: 'beograd',
      image: '/assets/images/mi-store-beo.webp'
    },
    {
      id: 4,
      name: 'Xiaomi Store Galerija',
      address: 'Bulevar Vudroa Vilsona 14',
      city: 'Belgrade Waterfront',
      phone: '060/5600-607',
      hours: 'Pon-Ned: 10:00-22:00h',
      coordinates: { lat: 44.8078, lng: 20.4489 },
      region: 'beograd',
      image: '/assets/images/mi-store-galerija.webp'
    },
    {
      id: 5,
      name: 'Xiaomi Store Delta City',
      address: 'Jurija Gagarina 16',
      city: 'Novi Beograd 11070',
      phone: '060/5600-647',
      hours: 'Pon-Ned: 10:00-22:00h',
      coordinates: { lat: 44.8099, lng: 20.4203 },
      region: 'beograd',
      image: '/assets/images/mi-store-delta.webp'
    }
  ], []);

  const otherCitiesStores = useMemo(() => [
    {
      id: 6,
      name: 'Xiaomi Store Novi Sad',
      address: 'Sentandrejski put 11',
      city: 'Novi Sad',
      phone: '060/5600-593',
      hours: 'Pon-Ned: 10:00-22:00h',
      coordinates: { lat: 45.2396, lng: 19.8227 },
      region: 'ostali',
      image: '/assets/images/mi-store-ns.webp'
    },
    {
      id: 7,
      name: 'Xiaomi Store Niš',
      address: 'Bulevar Nemanjića 11b',
      city: 'Niš',
      phone: '060/5600-614',
      hours: 'Pon-Ned: 10:30-21:30h',
      coordinates: { lat: 43.3209, lng: 21.8958 },
      region: 'ostali',
      image: '/assets/images/mi-store-nis.webp'
    },
    {
      id: 8,
      name: 'Xiaomi Store Kragujevac',
      address: 'Bulevar kraljice Marije 56',
      city: 'Kragujevac',
      phone: '060/5600-662',
      hours: 'Pon-Ned: 10:00-22:00h',
      coordinates: { lat: 44.0165, lng: 20.9114 },
      region: 'ostali',
      image: '/assets/images/mi-store-kg.webp'
    }
  ], []);

  const totalStoresCount = belgradStores.length + otherCitiesStores.length;

  const filteredStores = useMemo(() => {
    if (selectedFilter === 'beograd') return belgradStores;
    if (selectedFilter === 'ostali') return otherCitiesStores;
    return [...belgradStores, ...otherCitiesStores];
  }, [selectedFilter, belgradStores, otherCitiesStores]);

  // Dinamičko generisanje šeme prodavnica za Google
  const storeLocationsSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Xiaomi Store prodavnice u Srbiji - Lokacije",
    "description": "Lista svih autorizovanih Xiaomi Store prodavnica u Beogradu, Novom Sadu, Nišu i Kragujevcu.",
    "itemListElement": [...belgradStores, ...otherCitiesStores].map((store, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Store",
        "name": store.name,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": store.address,
          "addressLocality": store.city.split(' ')[0],
          "addressCountry": "RS"
        },
        "telephone": store.phone
      }
    }))
  };

  return (
    <>
      <SEO 
        titleKey="miStore.title" 
        descriptionKey="miStore.description" 
        pagePath="xiaomi" 
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(storeLocationsSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <main className="pt-20 flex flex-col min-h-[calc(100vh-80px)]">
          <div className="container mx-auto px-6 lg:px-8">
            <Breadcrumb />
          </div>
          <MiStoreHero />
          
          <section id="stores-map" className={`py-16 lg:py-24 ${theme === 'dark' ? 'bg-[#0F1115]' : 'bg-[#F3F4F6]'}`}>
            <div className="container mx-auto px-6 lg:px-8">
              <div className="flex justify-center mb-12">
                <div className="bg-card/50 border border-border/50 rounded-2xl p-2 flex flex-col md:flex-row w-full md:w-auto gap-2 md:gap-0">
                  <button
                    onClick={() => setSelectedFilter('svi')}
                    className={`px-3 md:px-6 py-2 md:py-3 rounded-xl text-sm md:text-base font-medium transition-all duration-300 flex items-center justify-center md:justify-start space-x-1 md:space-x-2 ${
                      selectedFilter === 'svi' ? 'bg-primary text-primary-foreground shadow-lg' : 'text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name="MapPin" size={16} className="md:w-[18px] md:h-[18px]" />
                    <span>{t('allLocations')} ({totalStoresCount})</span>
                  </button>
                  <button
                    onClick={() => setSelectedFilter('beograd')}
                    className={`px-3 md:px-6 py-2 md:py-3 rounded-xl text-sm md:text-base font-medium transition-all duration-300 flex items-center justify-center md:justify-start space-x-1 md:space-x-2 ${
                      selectedFilter === 'beograd' ? 'bg-primary text-primary-foreground shadow-lg' : 'text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name="Building2" size={16} className="md:w-[18px] md:h-[18px]" />
                    <span>{t('belgrade')} ({belgradStores.length})</span>
                  </button>
                  <button
                    onClick={() => setSelectedFilter('ostali')}
                    className={`px-3 md:px-6 py-2 md:py-3 rounded-xl text-sm md:text-base font-medium transition-all duration-300 flex items-center justify-center md:justify-start space-x-1 md:space-x-2 ${
                      selectedFilter === 'ostali' ? 'bg-primary text-primary-foreground shadow-lg' : 'text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name="MapPin" size={16} className="md:w-[18px] md:h-[18px]" />
                    <span>{t('otherCities')} ({otherCitiesStores.length})</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                <div className="xl:col-span-1">
                  <div className="bg-card/80 border border-border/50 rounded-2xl p-6 shadow-lg h-full">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-4">
                        <Icon name="Store" size={24} className="text-primary" />
                      </div>
                      <h3 className="text-xl font-bold text-foreground">
                        {selectedFilter === 'beograd' ? t('belgradeStores') :
                         selectedFilter === 'ostali' ? t('otherStores') :
                         t('allStores')}
                      </h3>
                    </div>
                    <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                      {filteredStores.map((store) => (
                        <div 
                          key={store.id} 
                          className="border border-border/30 rounded-xl p-4 hover:border-primary/50 transition-all duration-300 cursor-pointer hover:shadow-md bg-background/40"
                          onClick={() => setSelectedStore(store)}
                        >
                          <h4 className="font-semibold text-foreground mb-2 text-sm">{store.name}</h4>
                          <div className="space-y-1 text-xs">
                            <p className="text-muted-foreground flex items-center">
                              <Icon name="MapPin" size={14} className="mr-2 text-primary" />
                              {store.address}, {store.city}
                            </p>
                            <p className="text-primary font-medium flex items-center">
                              <Icon name="Phone" size={14} className="mr-2" />
                              {store.phone}
                            </p>
                            <p className="text-muted-foreground flex items-center">
                              <Icon name="Clock" size={14} className="mr-2 text-green-500" />
                              {store.hours}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="xl:col-span-2">
                  <div className="bg-card/80 border border-border/50 rounded-2xl p-6 shadow-lg h-full min-h-[400px]">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mr-4">
                          <Icon name="Map" size={24} className="text-accent" />
                        </div>
                        <h3 className="text-xl font-bold text-foreground">{t('interactiveMap')}</h3>
                      </div>
                      <div className="hidden md:block text-sm text-muted-foreground italic">
                        {t('clickPin')}
                      </div>
                    </div>
                    <StoresMap 
                      stores={filteredStores} 
                      onStoreSelect={setSelectedStore}
                      selectedStore={selectedStore}
                      selectedFilter={selectedFilter}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {selectedStore && (
            <div className="fixed inset-0 bg-background/90 z-[100] flex items-center justify-center p-4 md:p-6 backdrop-blur-sm" onClick={() => setSelectedStore(null)}>
              <div 
                className="bg-card border border-border rounded-2xl p-4 md:p-6 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-200 mt-20"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-4">
                      <Icon name="Store" size={24} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground">{selectedStore.name}</h3>
                      <p className="text-muted-foreground text-sm">{selectedStore.city}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedStore(null)}
                    className="w-10 h-10 bg-muted/20 rounded-full flex items-center justify-center hover:bg-muted/40 transition-colors"
                  >
                    <Icon name="X" size={20} className="text-foreground" />
                  </button>
                </div>

                <div className="relative w-full h-36 md:h-48 rounded-xl mb-6 overflow-hidden bg-muted/10">
                  {selectedStore.image ? (
                    <picture>
                      <source media="(max-width: 768px)" srcSet={selectedStore.image.replace('.webp', '-mobile.webp')} />
                      <img
                        src={selectedStore.image}
                        alt={selectedStore.name}
                        className={`w-full h-full object-cover transition-transform duration-500 hover:scale-125 ${selectedStore.id === 8 ? 'scale-125' : ''}`}
                        style={selectedStore.id === 8 ? { objectPosition: 'center top' } : undefined}
                      />
                    </picture>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Store" size={48} className="text-primary/20" />
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="MapPin" size={20} className="text-primary mt-1" />
                    <div>
                      <p className="font-semibold text-foreground text-sm">{t('address')}</p>
                      <p className="text-muted-foreground text-sm">{selectedStore.address}, {selectedStore.city}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Phone" size={20} className="text-green-500" />
                    <div>
                      <p className="font-semibold text-foreground text-sm">{t('phone')}</p>
                      <a href={`tel:${selectedStore.phone.replace(/[^0-9]/g, '')}`} className="text-primary hover:underline text-sm font-medium">{selectedStore.phone}</a>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Clock" size={20} className="text-orange-400" />
                    <div>
                      <p className="font-semibold text-foreground text-sm">{t('workHours')}</p>
                      <p className="text-muted-foreground text-sm">{selectedStore.hours}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-8">
                  <button 
                    onClick={() => {
                      const address = `${selectedStore.address}, ${selectedStore.city}`;
                      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`);
                    }}
                    className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-3 rounded-xl font-bold transition-all flex items-center justify-center space-x-2 shadow-md active:scale-95"
                  >
                    <Icon name="Navigation" size={18} />
                    <span>{t('findUs')}</span>
                  </button>
                  <button 
                    onClick={() => window.open(`tel:${selectedStore.phone.replace(/[^0-9]/g, '')}`, '_self')}
                    className="flex-1 border border-primary/30 text-primary hover:bg-primary/5 px-4 py-3 rounded-xl font-bold transition-all flex items-center justify-center space-x-2 active:scale-95"
                  >
                    <Icon name="Phone" size={18} />
                    <span>{t('call')}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <MiGallerySection />
        </main>
      </div>
    </>
  );
};

export default MiStorePage;